import React from "react";

import BackIcon from "@uui/ArrowLeft";
import CloseIcon from "@uui/XClose";

import Box, { BoxProps } from "@mui/material/Box";
import IconButton, { IconButtonProps } from "@mui/material/IconButton";
import styled from "@mui/material/styles/styled";
import Typography, { TypographyProps } from "@mui/material/Typography";

import { ELLIPSIS_SX } from "@app/common/styles";

import Stack, { StackProps } from "@rui/Stack";

/** Will apply a rounded outline to any element. Requires that the parent element be relatively positioned. */
export const roundedOutline = ({
  border,
  borderRadius,
  borderColor,
}: {
  border: React.CSSProperties["border"];
  borderRadius: React.CSSProperties["borderRadius"];
  borderColor?: React.CSSProperties["borderColor"];
}): { "&::after": React.CSSProperties } => ({
  "&::after": {
    pointerEvents: "none",
    content: "''",
    position: "absolute",
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    borderRadius,
    border,
    borderColor,
  },
});

export const BackButton = styled((props: IconButtonProps) => (
  <IconButton {...props} size="small">
    <BackIcon />
  </IconButton>
))(({ theme }) => ({
  color: theme.palette.shades.neutral[200],
  borderRadius: "6px",
  "& svg": {
    height: "20px",
    width: "20px",
  },
}));

export const Bold = styled("span")({
  fontWeight: 500,
});

export const Italic = styled("span")({
  fontStyle: "italic",
});

export const StickyContainer = styled(({ children, ...props }: StackProps) => (
  <Stack {...props}>{children}</Stack>
))({
  position: "sticky",
  top: 0,
  zIndex: 10,
});

/** Span that sx styles can be applied to */
export const Span = (props: BoxProps) => <Box {...props} component="span" />;

export const List = styled("ul")({
  marginBlockStart: "4px",
  marginBlockEnd: 0,
  paddingInlineStart: "24px",
});

export const Img = (props: BoxProps<"img">) => (
  <Box {...props} component="img" />
);

export const ModalCloseButton = styled<React.FC<IconButtonProps>>((props) => (
  <IconButton {...props}>
    <CloseIcon />
  </IconButton>
))(({ theme }) => ({
  border: "1px solid",
  borderColor: theme.palette.shades.neutral[100],
  borderRadius: "8px",
  height: "32px",
  width: "32px",
}));

type ClampedTypographyProps = TypographyProps & { maxLines: number };

export const ClampedTypography = styled<React.FC<ClampedTypographyProps>>(
  ({ maxLines: _l, ...props }) => <Typography {...props} />
)(({ maxLines }) => ({
  ...ELLIPSIS_SX,
  // if the browser supports line clamping, clamp to X lines instead of 1
  [`@supports( -webkit-line-clamp: ${maxLines})`]: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "initial",
    display: "-webkit-box",
    WebkitLineClamp: maxLines,
    WebkitBoxOrient: "vertical",
  },
}));
